import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Box,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';
import { DISTRICTS, DISTRICT_TALUKA_MAP } from '../constants/locations';

function Users() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [talukaFilter, setTalukaFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [talukaOptions, setTalukaOptions] = useState([]);
  const [editTalukaOptions, setEditTalukaOptions] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addFormData, setAddFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    mobile: '',
    role: 'ASHA_WORKER',
    asha_code: '',
    district: '',
    block: '',
    village: '',
  });
  const [addTalukaOptions, setAddTalukaOptions] = useState([]);

  useEffect(() => {
    if (districtFilter) {
      setTalukaOptions(DISTRICT_TALUKA_MAP[districtFilter] || []);
    } else {
      setTalukaOptions([]);
      setTalukaFilter('');
    }
  }, [districtFilter]);

  useEffect(() => {
    if (editFormData.district) {
      setEditTalukaOptions(DISTRICT_TALUKA_MAP[editFormData.district] || []);
    } else {
      setEditTalukaOptions([]);
    }
  }, [editFormData.district]);

  useEffect(() => {
    if (addFormData.district) {
      setAddTalukaOptions(DISTRICT_TALUKA_MAP[addFormData.district] || []);
    } else {
      setAddTalukaOptions([]);
    }
  }, [addFormData.district]);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, districtFilter, talukaFilter, activeFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      };
      if (search) params.search = search;
      if (districtFilter) params.district = districtFilter;
      if (talukaFilter) params.taluka = talukaFilter;
      if (activeFilter !== '') params.is_active = activeFilter === 'true';

      const response = await adminAPI.getUsers(params);
      setUsers(response.data.users);
      setTotal(response.data.total);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleReset = () => {
    setSearch('');
    setDistrictFilter('');
    setTalukaFilter('');
    setActiveFilter('');
    setPage(0);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditFormData({
      email: user.email,
      full_name: user.full_name,
      mobile: user.mobile || '',
      role: user.role,
      asha_code: user.asha_code || '',
      district: user.district || '',
      block: user.block || '',
      village: user.village || '',
      is_active: user.is_active,
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
    setEditFormData({});
  };

  const handleSaveUser = async () => {
    try {
      await adminAPI.updateUser(selectedUser.id, editFormData);
      handleCloseEditDialog();
      fetchUsers();
    } catch (err) {
      console.error('Failed to update user:', err);
      alert(err.response?.data?.detail || 'वापरकर्ता अद्यतनित करण्यात अयशस्वी');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (window.confirm(`${userName} यांना निष्क्रिय करायचे आहे का?`)) {
      try {
        await adminAPI.deleteUser(userId);
        fetchUsers();
      } catch (err) {
        console.error('Failed to delete user:', err);
        alert(err.response?.data?.detail || 'वापरकर्ता निष्क्रिय करण्यात अयशस्वी');
      }
    }
  };

  const handleOpenAddDialog = () => {
    setAddFormData({
      email: '',
      full_name: '',
      password: '',
      mobile: '',
      role: 'ASHA_WORKER',
      asha_code: '',
      district: '',
      block: '',
      village: '',
    });
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setAddFormData({
      email: '',
      full_name: '',
      password: '',
      mobile: '',
      role: 'ASHA_WORKER',
      asha_code: '',
      district: '',
      block: '',
      village: '',
    });
  };

  const handleAddUser = async () => {
    try {
      await adminAPI.createUser(addFormData);
      handleCloseAddDialog();
      fetchUsers();
    } catch (err) {
      console.error('Failed to add user:', err);
      alert(err.response?.data?.detail || 'वापरकर्ता तयार करण्यात अयशस्वी');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">वापरकर्ते व्यवस्थापन</Typography>
        <Box>
          <Button
            variant="contained"
            onClick={handleOpenAddDialog}
            sx={{ mr: 2 }}
          >
            + नवीन वापरकर्ता जोडा
          </Button>
          <IconButton onClick={handleReset} color="primary" title="फिल्टर रीसेट करा">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="शोधा"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="नाव, ईमेल किंवा मोबाइल"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              size="small"
              options={DISTRICTS}
              getOptionLabel={(option) => option.label}
              value={DISTRICTS.find(d => d.value === districtFilter) || null}
              onChange={(e, newValue) => setDistrictFilter(newValue?.value || '')}
              renderInput={(params) => (
                <TextField {...params} label="जिल्हा" />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              size="small"
              options={talukaOptions}
              getOptionLabel={(option) => option.label}
              value={talukaOptions.find(t => t.value === talukaFilter) || null}
              onChange={(e, newValue) => setTalukaFilter(newValue?.value || '')}
              disabled={!districtFilter}
              renderInput={(params) => (
                <TextField {...params} label="तालुका" />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>स्थिती</InputLabel>
              <Select
                value={activeFilter}
                label="स्थिती"
                onChange={(e) => setActiveFilter(e.target.value)}
              >
                <MenuItem value="">सर्व</MenuItem>
                <MenuItem value="true">सक्रिय</MenuItem>
                <MenuItem value="false">निष्क्रिय</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>नाव</TableCell>
                  <TableCell>ईमेल</TableCell>
                  <TableCell>मोबाइल</TableCell>
                  <TableCell>आशा कोड</TableCell>
                  <TableCell>जिल्हा</TableCell>
                  <TableCell>तालुका</TableCell>
                  <TableCell>गाव</TableCell>
                  <TableCell>स्थिती</TableCell>
                  <TableCell>क्रिया</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.full_name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.mobile || '-'}</TableCell>
                    <TableCell>{user.asha_code || '-'}</TableCell>
                    <TableCell>
                      {user.district
                        ? DISTRICTS.find(d => d.value === user.district)?.label || user.district
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {user.block
                        ? DISTRICT_TALUKA_MAP[user.district]?.find(t => t.value === user.block)?.label || user.block
                        : '-'}
                    </TableCell>
                    <TableCell>{user.village || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? 'सक्रिय' : 'निष्क्रिय'}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditUser(user)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteUser(user.id, user.full_name)}
                        disabled={!user.is_active}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="प्रति पृष्ठ पंक्ती:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} पैकी ${count}`}
            />
          </>
        )}
      </TableContainer>

      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>वापरकर्ता संपादित करा</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="संपूर्ण नाव"
                value={editFormData.full_name || ''}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, full_name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="मोबाइल नंबर"
                value={editFormData.mobile || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setEditFormData({ ...editFormData, mobile: value });
                }}
                placeholder="10 अंकी मोबाइल नंबर"
                helperText="10 अंकी मोबाइल नंबर आवश्यक आहे"
                error={editFormData.mobile && editFormData.mobile.length !== 10}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ईमेल (पर्यायी)"
                type="email"
                value={editFormData.email || ''}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="आशा कोड"
                value={editFormData.asha_code || ''}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, asha_code: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={DISTRICTS}
                getOptionLabel={(option) => option.label}
                value={DISTRICTS.find(d => d.value === editFormData.district) || null}
                onChange={(e, newValue) => {
                  setEditFormData({ ...editFormData, district: newValue?.value || '', block: '' });
                }}
                renderInput={(params) => <TextField {...params} label="जिल्हा" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={editTalukaOptions}
                getOptionLabel={(option) => option.label}
                value={editTalukaOptions.find(t => t.value === editFormData.block) || null}
                onChange={(e, newValue) => {
                  setEditFormData({ ...editFormData, block: newValue?.value || '' });
                }}
                disabled={!editFormData.district}
                renderInput={(params) => <TextField {...params} label="तालुका" />}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="गाव"
                value={editFormData.village || ''}
                onChange={(e) => setEditFormData({ ...editFormData, village: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>भूमिका</InputLabel>
                <Select
                  value={editFormData.role || 'ASHA_WORKER'}
                  label="भूमिका"
                  onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                >
                  <MenuItem value="ASHA_WORKER">आशा कार्यकर्ता</MenuItem>
                  <MenuItem value="ADMIN">प्रशासक</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>स्थिती</InputLabel>
                <Select
                  value={editFormData.is_active ?? true}
                  label="स्थिती"
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, is_active: e.target.value })
                  }
                >
                  <MenuItem value={true}>सक्रिय</MenuItem>
                  <MenuItem value={false}>निष्क्रिय</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>रद्द करा</Button>
          <Button onClick={handleSaveUser} variant="contained">
            जतन करा
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>नवीन वापरकर्ता जोडा</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="संपूर्ण नाव"
                value={addFormData.full_name}
                onChange={(e) =>
                  setAddFormData({ ...addFormData, full_name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="मोबाइल नंबर"
                value={addFormData.mobile}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setAddFormData({ ...addFormData, mobile: value });
                }}
                placeholder="10 अंकी मोबाइल नंबर"
                helperText="10 अंकी मोबाइल नंबर आवश्यक आहे"
                error={addFormData.mobile && addFormData.mobile.length !== 10}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ईमेल (पर्यायी)"
                type="email"
                value={addFormData.email}
                onChange={(e) => setAddFormData({ ...addFormData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="पासवर्ड"
                type="password"
                value={addFormData.password}
                onChange={(e) => setAddFormData({ ...addFormData, password: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="आशा कोड"
                value={addFormData.asha_code}
                onChange={(e) =>
                  setAddFormData({ ...addFormData, asha_code: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={DISTRICTS}
                getOptionLabel={(option) => option.label}
                value={DISTRICTS.find(d => d.value === addFormData.district) || null}
                onChange={(e, newValue) => {
                  setAddFormData({ ...addFormData, district: newValue?.value || '', block: '' });
                }}
                renderInput={(params) => <TextField {...params} label="जिल्हा" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={addTalukaOptions}
                getOptionLabel={(option) => option.label}
                value={addTalukaOptions.find(t => t.value === addFormData.block) || null}
                onChange={(e, newValue) => {
                  setAddFormData({ ...addFormData, block: newValue?.value || '' });
                }}
                disabled={!addFormData.district}
                renderInput={(params) => <TextField {...params} label="तालुका" />}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="गाव"
                value={addFormData.village}
                onChange={(e) => setAddFormData({ ...addFormData, village: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>भूमिका</InputLabel>
                <Select
                  value={addFormData.role}
                  label="भूमिका"
                  onChange={(e) => setAddFormData({ ...addFormData, role: e.target.value })}
                >
                  <MenuItem value="ASHA_WORKER">आशा कार्यकर्ता</MenuItem>
                  <MenuItem value="ADMIN">प्रशासक</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>रद्द करा</Button>
          <Button onClick={handleAddUser} variant="contained">
            जोडा
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Users;
